<template>
  <div>
    <div style="text-align: center">
      <h3>Import User dari Excel / CSV</h3>
    </div>
    <br />

    <div v-if="stage === 'pick'">
      <p class="text-muted" style="font-size: 13px">
        Upload file <b>.xlsx</b>, <b>.xls</b>, atau <b>.csv</b>. Kolom wajib:
        <code>name</code>, <code>username</code>, <code>email</code>,
        <code>phone</code>, <code>password</code>, <code>package_id</code>.
      </p>

      <div style="margin-bottom: 15px">
        <a
          href="#"
          @click.prevent="downloadTemplate"
          class="btn btn-sm btn-outline-secondary"
        >
          <i class="fas fa-download"></i> Download Template
        </a>
        <a
          href="#"
          @click.prevent="togglePackageList"
          class="btn btn-sm btn-outline-info"
          style="margin-left: 5px"
        >
          <i class="fas fa-list"></i>
          {{ showPackages ? "Sembunyikan" : "Lihat" }} Daftar package_id
        </a>
      </div>

      <div
        v-if="showPackages"
        class="border rounded p-2 mb-3"
        style="max-height: 180px; overflow-y: auto; font-size: 12px"
      >
        <table class="table table-sm table-striped mb-0">
          <thead>
            <tr><th>ID</th><th>Nama Paket</th></tr>
          </thead>
          <tbody>
            <tr v-for="p in packages" :key="p.id">
              <td>{{ p.id }}</td>
              <td>{{ p.name }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <label>Pilih File</label>
      <input
        type="file"
        class="form-control"
        accept=".xlsx,.xls,.csv"
        @change="onFileChange"
      />
    </div>

    <div v-if="stage === 'preview'">
      <p>
        Ditemukan <b>{{ rows.length }}</b> baris:
        <span class="text-success">{{ validCount }} valid</span>,
        <span class="text-danger">{{ invalidCount }} invalid</span>.
      </p>
      <div style="max-height: 300px; overflow: auto" class="border rounded">
        <table class="table table-sm mb-0" style="font-size: 12px">
          <thead>
            <tr>
              <th>#</th>
              <th>Status</th>
              <th>Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Package ID</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in rows" :key="i">
              <td>{{ i + 1 }}</td>
              <td>
                <span v-if="r._errors.length === 0" class="badge badge-success">OK</span>
                <span v-else class="badge badge-danger">Invalid</span>
              </td>
              <td>{{ r.name }}</td>
              <td>{{ r.username }}</td>
              <td>{{ r.email }}</td>
              <td>{{ r.phone }}</td>
              <td>{{ r.package_id }}</td>
              <td style="color: #dc3545; font-size: 11px">
                {{ r._errors.join(", ") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style="margin-top: 15px">
        <button
          class="btn btn-secondary"
          @click="resetAll"
          :disabled="importing"
        >
          Batal / Pilih Ulang
        </button>
        <button
          class="btn btn-primary"
          @click="startImport"
          :disabled="importing || validCount === 0"
          style="margin-left: 5px"
        >
          Import {{ validCount }} User
        </button>
      </div>
    </div>

    <div v-if="stage === 'importing' || stage === 'done'">
      <p>
        Progress: <b>{{ progress.done }} / {{ progress.total }}</b>
        <span class="text-success" style="margin-left: 10px"
          >&check; {{ progress.success }} berhasil</span
        >
        <span class="text-danger" style="margin-left: 10px"
          >&times; {{ progress.failed }} gagal</span
        >
      </p>
      <div class="progress" style="height: 10px">
        <div
          class="progress-bar"
          role="progressbar"
          :style="{
            width: progress.total ? (progress.done / progress.total) * 100 + '%' : '0%',
          }"
        ></div>
      </div>

      <div
        v-if="progress.failed > 0"
        style="margin-top: 15px; max-height: 200px; overflow-y: auto"
      >
        <h6>Baris yang gagal:</h6>
        <ul class="text-danger" style="font-size: 12px">
          <li v-for="(e, i) in failedDetails" :key="i">
            Baris {{ e.row }} ({{ e.email }}): {{ e.message }}
          </li>
        </ul>
      </div>

      <div v-if="stage === 'done'" style="margin-top: 15px">
        <button class="btn btn-primary" @click="closeAndRefresh">
          Selesai
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

const REQUIRED_FIELDS = [
  "name",
  "username",
  "email",
  "phone",
  "password",
  "package_id",
];

export default {
  data() {
    return {
      stage: "pick",
      rows: [],
      packages: [],
      showPackages: false,
      importing: false,
      progress: { total: 0, done: 0, success: 0, failed: 0 },
      failedDetails: [],
    };
  },
  computed: {
    validCount() {
      return this.rows.filter((r) => r._errors.length === 0).length;
    },
    invalidCount() {
      return this.rows.filter((r) => r._errors.length > 0).length;
    },
  },
  mounted() {
    this.loadPackages();
  },
  methods: {
    async loadPackages() {
      try {
        let p = await this.$store.dispatch("package_list");
        this.packages = p.data.data.packages || [];
      } catch (err) {
        // silent fail; user still can input package_id manually
      }
    },
    togglePackageList() {
      this.showPackages = !this.showPackages;
    },
    downloadTemplate() {
      const headers = REQUIRED_FIELDS;
      const example = [
        {
          name: "Budi Santoso",
          username: "budisan",
          email: "budi@example.com",
          phone: "628123456789",
          password: "Rahasia123",
          package_id: this.packages[0] ? this.packages[0].id : 1,
        },
      ];
      const ws = XLSX.utils.json_to_sheet(example, { header: headers });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      XLSX.writeFile(wb, "starsender_import_template.xlsx");
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = new Uint8Array(ev.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const sheetName = wb.SheetNames[0];
          const ws = wb.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
          if (json.length === 0) {
            this.$toast.error("File kosong atau sheet pertama tidak ada data.");
            return;
          }
          this.rows = json.map((r) => this.normalizeAndValidate(r));
          this.stage = "preview";
        } catch (err) {
          this.$toast.error("Gagal membaca file: " + err.message);
        }
      };
      reader.onerror = () => {
        this.$toast.error("Gagal membaca file.");
      };
      reader.readAsArrayBuffer(file);
    },
    normalizeAndValidate(raw) {
      const row = {
        name: String(raw.name || raw.Name || "").trim(),
        username: String(raw.username || raw.Username || "").trim(),
        email: String(raw.email || raw.Email || "").trim(),
        phone: String(raw.phone || raw.Phone || "").trim(),
        password: String(raw.password || raw.Password || ""),
        package_id: raw.package_id || raw.PackageId || raw["Package ID"] || "",
        _errors: [],
      };

      for (const f of REQUIRED_FIELDS) {
        if (row[f] === "" || row[f] === null || row[f] === undefined) {
          row._errors.push(f + " kosong");
        }
      }

      if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
        row._errors.push("email tidak valid");
      }

      const pid = parseInt(row.package_id, 10);
      if (row.package_id !== "" && (isNaN(pid) || pid <= 0)) {
        row._errors.push("package_id harus angka");
      } else {
        row.package_id = pid;
      }

      return row;
    },
    async startImport() {
      this.importing = true;
      this.stage = "importing";
      const validRows = this.rows
        .map((r, idx) => ({ r, idx }))
        .filter((x) => x.r._errors.length === 0);

      this.progress = {
        total: validRows.length,
        done: 0,
        success: 0,
        failed: 0,
      };
      this.failedDetails = [];

      for (const { r, idx } of validRows) {
        const payload = {
          name: r.name,
          username: r.username,
          email: r.email,
          phone: r.phone,
          password: r.password,
          package: r.package_id,
        };
        try {
          await this.$store.dispatch("admin_user_create", payload);
          this.progress.success++;
        } catch (err) {
          this.progress.failed++;
          const msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : err.message || "Unknown error";
          this.failedDetails.push({
            row: idx + 1,
            email: r.email,
            message: msg,
          });
        }
        this.progress.done++;
      }

      this.stage = "done";
      this.importing = false;
      if (this.progress.success > 0) {
        this.$toast.success(
          this.progress.success + " user berhasil dibuat."
        );
      }
      if (this.progress.failed > 0) {
        this.$toast.error(this.progress.failed + " baris gagal.");
      }
    },
    resetAll() {
      this.stage = "pick";
      this.rows = [];
      this.progress = { total: 0, done: 0, success: 0, failed: 0 };
      this.failedDetails = [];
    },
    closeAndRefresh() {
      this.resetAll();
      this.$root.$emit("toggle_modal", "import_user");
      this.$root.$emit("refresh_table", true);
    },
  },
};
</script>

<style scoped>
code {
  background: #f1f1f1;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}
</style>
